import { buildDoubleSixSet, type Tile } from './types.js';
import type { DominoRuleSet, PlayerCount } from './variants/types.js';

/**
 * Deterministic LCG (Numerical Recipes constants). Used so a seed completely
 * determines the deal, which lets history/replay reproduce a hand exactly.
 */
export interface Rng {
  next(): number;
}

export function lcgRng(seed: number): Rng {
  let state = (seed >>> 0) || 1;
  return {
    next(): number {
      state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
      return state / 0x100000000;
    },
  };
}

/** Fisher–Yates with a supplied RNG so the shuffle is fully deterministic. */
export function shuffle<T>(items: readonly T[], rng: Rng): T[] {
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng.next() * (i + 1));
    const a = out[i];
    const b = out[j];
    if (a !== undefined && b !== undefined) {
      out[i] = b;
      out[j] = a;
    }
  }
  return out;
}

export interface DealResult {
  readonly hands: readonly (readonly Tile[])[];
  readonly boneyard: readonly Tile[];
}

/**
 * Deal `playerCount` hands of `ruleSet.handSize[playerCount]` tiles each from
 * a shuffled set; the remainder forms the boneyard. The boneyard order is
 * preserved so subsequent draws are deterministic given the seed.
 */
export function dealHands(
  ruleSet: DominoRuleSet,
  playerCount: PlayerCount,
  seed: number,
): DealResult {
  if (ruleSet.setSize !== 28 || ruleSet.maxPip !== 6) {
    throw new Error('dealHands: only the double-six set is supported in V1');
  }
  const tiles = buildDoubleSixSet();
  const shuffled = shuffle(tiles, lcgRng(seed));
  const handSize = ruleSet.handSize[playerCount];
  const hands: Tile[][] = [];
  let cursor = 0;
  for (let p = 0; p < playerCount; p += 1) {
    hands.push(shuffled.slice(cursor, cursor + handSize));
    cursor += handSize;
  }
  const boneyard = shuffled.slice(cursor);
  return { hands, boneyard };
}
